import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Typography, Empty, Button, Space, Input, Select, Tag, Collapse, message, Switch } from 'antd';
import { FileText, Folder, Clock, Plus, Search, Calendar, Hash, BarChart2, Delete } from 'lucide-react';
import { format } from 'date-fns';
import { Document } from '../types';
import { AppSettings } from '../utils/SettingsManager';
import ActivityHeatmap from './ActivityHeatmap';
import { getAllTags } from '../services/api';
import authService from '../services/authService';
import api from '../services/api';
import cacheManager from '../services/cacheManager';

const { Title, Text } = Typography;
const { Search: AntSearch } = Input;
const { Option } = Select;
const { Panel } = Collapse;

interface DashboardProps {
  documents: Document[];
  onDocumentSelect: (doc: Document) => void;
  onDocumentDelete: (id: string) => void;
  settings: AppSettings;
}

interface TagWithDocuments {
  tag: string;
  color?: string;
  documents: Document[];
}

const Dashboard: React.FC<DashboardProps> = ({
  documents,
  onDocumentSelect,
  onDocumentDelete,
  settings,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'recent' | 'categories' | 'heatmap'>('recent');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [tagsWithDocuments, setTagsWithDocuments] = useState<TagWithDocuments[]>([]);
  const [allTags, setAllTags] = useState<any[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [noteStats, setNoteStats] = useState<any>(null);

  // 使用ref来防止React StrictMode导致的重复执行
  const hasInitialized = useRef(false);

  // 统一的初始化useEffect
  useEffect(() => {
    // 防止React StrictMode导致的重复执行
    if (hasInitialized.current) {
      console.log('Dashboard: 跳过重复初始化（React StrictMode）');
      return;
    }
    hasInitialized.current = true;

    console.log('Dashboard: 开始初始化数据');
    setIsAuthenticated(authService.isAuthenticated());
    loadNoteStats();
    // 立即加载标签数据
    loadTagsData();
  }, []);

  // 监听缓存初始化完成事件
  useEffect(() => {
    const handleCacheInitialized = () => {
      console.log('Dashboard: 收到缓存初始化完成事件，重新加载数据');
      loadTagsData();
      if (isAuthenticated) {
        loadNoteStats();
      }
    };

    cacheManager.on('initialized', handleCacheInitialized);

    return () => {
      cacheManager.off('initialized', handleCacheInitialized);
    };
  }, [isAuthenticated]);

  // 加载笔记统计数据
  const loadNoteStats = async () => {
    try {
      // 确保缓存已初始化
      await cacheManager.initialize();

      // 从缓存获取数据
      const cacheData = cacheManager.getData();
      if (cacheData.noteStats) {
        setNoteStats(cacheData.noteStats);
      }
    } catch (error) {
      // 静默处理获取笔记统计失败
      console.warn('加载笔记统计失败:', error);
    }
  };

  // 独立的标签数据加载函数
  const loadTagsData = async () => {
    console.log('Dashboard: 开始加载标签数据');

    if (!isAuthenticated) {
      // 本地模式：从文档中提取标签
      const localTags = getLocalTagsWithDocuments();
      setTagsWithDocuments(localTags);
      return;
    }

    try {
      // 等待缓存初始化完成
      await cacheManager.waitForInitialization();

      // 从缓存获取标签数据
      const cacheData = cacheManager.getData();
      const tags = cacheData.tags || [];

      console.log('Dashboard: 从缓存获取到标签数据:', tags.length);
      setAllTags(tags);

      if (tags.length === 0) {
        const localTags = getLocalTagsWithDocuments();
        setTagsWithDocuments(localTags);
        return;
      }

      // 立即设置标签数据，不等待documents
      const tagsWithDocs = tags.map((tag: any) => {
        const tagName = typeof tag === 'string' ? tag : (tag.name || tag.tag || '');
        const tagColor = typeof tag === 'string' ? undefined : tag.color;

        return {
          tag: tagName,
          color: tagColor,
          documents: [] // 初始为空，后续通过useEffect更新
        };
      });

      setTagsWithDocuments(tagsWithDocs);
      console.log('Dashboard: 标签数据设置完成:', tagsWithDocs.length);
    } catch (error) {
      console.warn('加载标签数据失败:', error);
      const localTags = getLocalTagsWithDocuments();
      setTagsWithDocuments(localTags);
    }
  };

  // 获取分类颜色
  const getCategoryColor = (category: string) => {
    const colors = ['blue', 'green', 'orange', 'red', 'purple', 'cyan', 'magenta', 'yellow'];
    const index = category.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // 加载标签数据
  useEffect(() => {
    // 只处理标签与文档的关联关系，不重新加载标签数据
    if (!isAuthenticated || allTags.length === 0) {
      if (!isAuthenticated) {
        // 本地模式：从文档中提取标签
        const localTags = getLocalTagsWithDocuments();
        setTagsWithDocuments(localTags);
      }
      return;
    }

    console.log('Dashboard: 更新标签与文档的关联关系', {
      tagsLength: allTags.length,
      documentsLength: documents.length
    });

    // 为每个标签收集对应的文档
    const tagsWithDocs = allTags.map((tag: any) => {
      const tagName = typeof tag === 'string' ? tag : (tag.name || tag.tag || '');
      const tagColor = typeof tag === 'string' ? undefined : tag.color;
      const tagId = typeof tag === 'string' ? null : tag.id;

      // 更全面的文档匹配逻辑
      const docsWithTag = documents.filter(doc => {
        // 检查tags数组
        const hasTagInArray = doc.tags && Array.isArray(doc.tags) && doc.tags.includes(tagName);
        // 检查tag对象的name属性
        const hasTagObjectName = doc.tag && doc.tag.name === tagName;
        // 检查tag对象的id属性
        const hasTagObjectId = tagId && doc.tag && doc.tag.id === tagId;
        // 检查是否有tagId字段
        const hasTagId = tagId && doc.tagId === tagId;

        return hasTagInArray || hasTagObjectName || hasTagObjectId || hasTagId;
      }).filter(doc => {
        if (!searchTerm) return true;
        const lowerSearchTerm = searchTerm.toLowerCase();
        return (
          doc.title.toLowerCase().includes(lowerSearchTerm) ||
          doc.content.toLowerCase().includes(lowerSearchTerm)
        );
      });

      return {
        tag: tagName,
        color: tagColor,
        documents: docsWithTag
      };
    });

    setTagsWithDocuments(tagsWithDocs);
  }, [allTags, documents, searchTerm, isAuthenticated]);

  // 本地标签提取函数
  const getLocalTagsWithDocuments = () => {
    const allTags = new Set<string>();

    documents.forEach(doc => {
      if (doc.tags && Array.isArray(doc.tags)) {
        doc.tags.forEach(tag => allTags.add(tag));
      }
      if (doc.tag && doc.tag.name) {
        allTags.add(doc.tag.name);
      }
    });

    return Array.from(allTags).map(tag => {
      const docsWithTag = documents.filter(doc => {
        const hasTagInArray = doc.tags && doc.tags.includes(tag);
        const hasTagObject = doc.tag && doc.tag.name === tag;
        return hasTagInArray || hasTagObject;
      }).filter(doc => {
        if (!searchTerm) return true;
        const lowerSearchTerm = searchTerm.toLowerCase();
        return (
          doc.title.toLowerCase().includes(lowerSearchTerm) ||
          doc.content.toLowerCase().includes(lowerSearchTerm)
        );
      });

      return {
        tag,
        color: undefined,
        documents: docsWithTag
      };
    });
  };

  // 过滤文档（使用与热力图相同的逻辑）
  const getFilteredDocuments = () => {
    let filtered = documents.filter(doc =>
      doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.content.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // 如果选择了特定日期，进一步过滤（显示有活动的文档）
    if (selectedDate && viewMode === 'recent') {
      const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
      filtered = filtered.filter(doc => {
        const createdAt = new Date(doc.createdAt);
        const updatedAt = new Date(doc.updatedAt || doc.createdAt);
        const createdDateStr = format(createdAt, 'yyyy-MM-dd');
        const updatedDateStr = format(updatedAt, 'yyyy-MM-dd');

        // 如果创建日期或更新日期匹配选中日期，则包含该文档
        return createdDateStr === selectedDateStr ||
          (createdDateStr !== updatedDateStr && updatedDateStr === selectedDateStr);
      });
    }

    // 按活动时间排序（优先更新时间，其次创建时间）
    return filtered.sort((a, b) => {
      const aTime = Math.max(new Date(a.updatedAt || a.createdAt).getTime(), new Date(a.createdAt).getTime());
      const bTime = Math.max(new Date(b.updatedAt || b.createdAt).getTime(), new Date(b.createdAt).getTime());
      return bTime - aTime;
    });
  };

  const filteredDocuments = getFilteredDocuments();

  // 按分类分组文档
  const documentsByCategory = filteredDocuments.reduce((acc, doc) => {
    const category = doc.category || '默认';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(doc);
    return acc;
  }, {} as Record<string, Document[]>);

  // 今日活动文档数量（创建+更新）
  const todayDocuments = documents.filter(doc => {
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    const createdAt = new Date(doc.createdAt);
    const updatedAt = new Date(doc.updatedAt || doc.createdAt);
    const createdDateStr = format(createdAt, 'yyyy-MM-dd');
    const updatedDateStr = format(updatedAt, 'yyyy-MM-dd');

    // 如果创建日期或更新日期是今天，则计入今日活动
    return createdDateStr === todayStr ||
      (createdDateStr !== updatedDateStr && updatedDateStr === todayStr);
  }).length;

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <Row gutter={[12, 12]}>
          {/* 统计信息 */}
          <Col span={24}>
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={8}>
                <Card>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FileText size={32} style={{ color: '#1890ff', marginRight: 16 }} />
                    <div>
                      <Title level={2} style={{ margin: 0 }}>
                        {isAuthenticated ? (noteStats?.totalNotes || documents.length) : documents.length}
                      </Title>
                      <Text type="secondary">{isAuthenticated ? '总笔记数' : '总文档数'}</Text>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Folder size={32} style={{ color: '#52c41a', marginRight: 16 }} />
                    <div>
                      <Title level={2} style={{ margin: 0 }}>
                        {isAuthenticated ? (noteStats?.totalTags || tagsWithDocuments.length) : tagsWithDocuments.length}
                      </Title>
                      <Text type="secondary">标签总数</Text>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Clock size={32} style={{ color: '#faad14', marginRight: 16 }} />
                    <div>
                      <Title level={2} style={{ margin: 0 }}>
                        {isAuthenticated ? (noteStats?.todayNotes || todayDocuments) : todayDocuments}
                      </Title>
                      <Text type="secondary">今日{isAuthenticated ? '笔记' : '文档'}</Text>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
          </Col>

          {/* 热力图 */}
          <Col span={24}>
            <Card className="heatmap-wrapper">
              <ActivityHeatmap
                documents={documents}
                key={`heatmap-${isAuthenticated}-${documents.length}`}
                onDateSelect={(date) => {
                  // 设置选中日期并切换到最近文档视图
                  setSelectedDate(date);
                  setViewMode('recent');
                  setSearchTerm(''); // 清空搜索条件

                  // 添加日期选择提示
                  const dateStr = format(date, 'yyyy-MM-dd');
                  const docsOnDate = documents.filter(doc => {
                    const createdAt = new Date(doc.createdAt);
                    const updatedAt = new Date(doc.updatedAt || doc.createdAt);
                    const createdDateStr = format(createdAt, 'yyyy-MM-dd');
                    const updatedDateStr = format(updatedAt, 'yyyy-MM-dd');

                    // 如果创建日期或更新日期匹配当前日期，则包含该文档
                    return createdDateStr === dateStr ||
                      (createdDateStr !== updatedDateStr && updatedDateStr === dateStr);
                  });

                  message.info(
                    `已选择${dateStr}，找到${docsOnDate.length}篇${isAuthenticated ? '笔记' : '文档'}`
                  );
                }}
              />
            </Card>
          </Col>

          {/* 文档列表 */}
          <Col span={24}>
            <Card className="document-list-card" style={{ marginBottom: '80px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Title level={3} style={{ margin: 0 }}>
                  {isAuthenticated ? '笔记列表' : '文档列表'}
                  {selectedDate && viewMode === 'recent' && (
                    <span style={{ fontSize: '14px', fontWeight: 'normal', marginLeft: '10px', color: '#1890ff' }}>
                      ({format(selectedDate, 'yyyy-MM-dd')})
                    </span>
                  )}
                </Title>
                <Space>
                  {selectedDate && viewMode === 'recent' && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => setSelectedDate(null)}
                    >
                      返回今日
                    </Button>
                  )}
                  <span>标签视图</span>
                  <Switch
                    checked={viewMode === 'recent'}
                    onChange={(checked) => {
                      setViewMode(checked ? 'recent' : 'categories');
                      // 如果从最近切换到分类，且没有选中日期，清除选中日期
                      if (!checked && !selectedDate) {
                        setSelectedDate(null);
                      }
                    }}
                  />
                  <span>最近{isAuthenticated ? '笔记' : '文档'}</span>
                </Space>
              </div>

              {/* 搜索框 */}
              <div style={{ marginBottom: 16 }}>
                <AntSearch
                  placeholder={`搜索${isAuthenticated ? '笔记' : '文档'}...`}
                  allowClear
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: '100%' }}
                  prefix={<Search size={16} />}
                />
              </div>
              {viewMode === 'categories' ? (
                // 标签视图
                <div>
                  {tagsWithDocuments.length === 0 ? (
                    <Empty description={searchTerm ? `没有符合"${searchTerm}"的笔记` : "暂无标签"} />
                  ) : (
                    tagsWithDocuments.map(({ tag, color, documents: tagDocuments }, index) => {
                      const isExpanded = expandedCategories.includes(tag);

                      return (
                        <div key={tag} style={{ marginBottom: 16 }}>
                          <Card
                            size="small"
                            style={{
                              border: '1px solid #f0f0f0',
                              borderRadius: '8px'
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: 'pointer',
                                padding: '8px 0'
                              }}
                              onClick={() => {
                                if (isExpanded) {
                                  setExpandedCategories(prev => prev.filter(cat => cat !== tag));
                                } else {
                                  setExpandedCategories(prev => [...prev, tag]);
                                }
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <Tag
                                  color={color || getCategoryColor(tag)}
                                  style={{
                                    fontSize: '14px',
                                    padding: '4px 12px',
                                    fontWeight: '500',
                                    borderRadius: '16px'
                                  }}
                                >
                                  <Hash size={12} style={{ marginRight: 4 }} />
                                  {tag}
                                </Tag>
                                <Text type="secondary">
                                  {tagDocuments.length} 篇文档
                                </Text>
                              </div>
                              <Button
                                type="text"
                                size="small"
                                style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                              >
                                ▼
                              </Button>
                            </div>

                            {isExpanded && (
                              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
                                <Row gutter={[12, 12]}>
                                  {tagDocuments.map(doc => (
                                    <Col xs={24} sm={12} lg={8} key={doc.id}>
                                      <Card
                                        size="small"
                                        hoverable
                                        onClick={() => onDocumentSelect(doc)}
                                        style={{
                                          cursor: 'pointer',
                                          height: '140px',
                                          display: 'flex',
                                          flexDirection: 'column'
                                        }}
                                        bodyStyle={{
                                          padding: '12px',
                                          height: '100%',
                                          display: 'flex',
                                          flexDirection: 'column',
                                          justifyContent: 'space-between'
                                        }}
                                      >
                                        <div style={{ flex: 1, overflow: 'hidden' }}>
                                          <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            gap: '8px',
                                            marginBottom: 8,
                                            minHeight: '20px'
                                          }}>
                                            <Text
                                              strong
                                              style={{
                                                fontSize: '14px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                flex: 1,
                                                minWidth: 0
                                              }}
                                              title={doc.title}
                                            >
                                              {doc.title}
                                            </Text>
                                            {/* 标签视图下不显示分类和标签 */}
                                          </div>
                                          <Text
                                            type="secondary"
                                            style={{
                                              fontSize: '12px',
                                              display: '-webkit-box',
                                              WebkitLineClamp: 2,
                                              WebkitBoxOrient: 'vertical',
                                              overflow: 'hidden',
                                              lineHeight: '1.4',
                                              height: '33.6px'
                                            }}
                                            title={doc.content}
                                          >
                                            {doc.content || '暂无内容'}
                                          </Text>
                                        </div>
                                        <div style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'flex-end',
                                          marginTop: 8,
                                          minHeight: '20px'
                                        }}>
                                          <Text type="secondary" style={{ fontSize: '11px', flexShrink: 0 }}>
                                            {format(new Date(doc.updatedAt), 'MM-dd HH:mm')}
                                          </Text>
                                          {/* 添加删除按钮 */}
                                          <Button
                                            size="small"
                                            danger
                                            icon={<Delete size={12} />}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onDocumentDelete(doc.id);
                                            }}
                                            style={{
                                              fontSize: '10px',
                                              height: '20px',
                                              padding: '0 6px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '2px'
                                            }}
                                          >
                                            删除
                                          </Button>
                                        </div>
                                      </Card>
                                    </Col>
                                  ))}
                                </Row>
                              </div>
                            )}
                          </Card>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : (
                <div>
                  {filteredDocuments.length === 0 ? (
                    <Empty description={
                      searchTerm
                        ? `没有符合"${searchTerm}"的${isAuthenticated ? '笔记' : '文档'}`
                        : selectedDate
                          ? `${format(selectedDate, 'yyyy-MM-dd')} 没有${isAuthenticated ? '笔记' : '文档'}`
                          : `今日暂无${isAuthenticated ? '笔记' : '文档'}`
                    } />
                  ) : (
                    <Row gutter={[12, 12]}>
                      {filteredDocuments.map(doc => (
                        <Col xs={24} sm={12} lg={8} key={doc.id}>
                          <Card
                            size="small"
                            hoverable
                            onClick={() => onDocumentSelect(doc)}
                            style={{
                              cursor: 'pointer',
                              height: '140px',
                              display: 'flex',
                              flexDirection: 'column'
                            }}
                            className="recent-note-card"
                            bodyStyle={{
                              padding: '12px',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between'
                            }}
                          >
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '8px',
                                marginBottom: 8,
                                minHeight: '20px'
                              }}>
                                <Text
                                  strong
                                  style={{
                                    fontSize: '14px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    flex: 1,
                                    minWidth: 0
                                  }}
                                  title={doc.title}
                                >
                                  {doc.title}
                                </Text>
                                {/* 显示标签在标题旁边 */}
                                {doc.tag && (
                                  <Tag
                                    color={doc.tag.color || getCategoryColor(doc.tag.name)}
                                    style={{
                                      fontSize: '10px',
                                      padding: '1px 6px',
                                      lineHeight: '14px',
                                      height: '16px',
                                      margin: 0,
                                      borderRadius: '8px',
                                      fontWeight: '400',
                                      flexShrink: 0
                                    }}
                                  >
                                    {doc.tag.name.length > 6 ? doc.tag.name.substring(0, 6) + '...' : doc.tag.name}
                                  </Tag>
                                )}
                                {/* 如果没有tag对象但有tags数组，显示第一个标签 */}
                                {!doc.tag && doc.tags && doc.tags.length > 0 && (() => {
                                  const tagName = doc.tags[0];
                                  const tagData = allTags?.find((t: any) => t.name === tagName);
                                  const tagColor = tagData?.color || getCategoryColor(tagName);
                                  return (
                                    <Tag
                                      color={tagColor}
                                      style={{
                                        fontSize: '10px',
                                        padding: '1px 6px',
                                        lineHeight: '14px',
                                        height: '16px',
                                        margin: 0,
                                        borderRadius: '8px',
                                        fontWeight: '400',
                                        flexShrink: 0
                                      }}
                                    >
                                      {tagName.length > 6 ? tagName.substring(0, 6) + '...' : tagName}
                                    </Tag>
                                  );
                                })()}
                              </div>
                              <Text
                                type="secondary"
                                style={{
                                  fontSize: '12px',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  lineHeight: '1.4',
                                  height: '33.6px'
                                }}
                                title={doc.content}
                              >
                                {doc.content || '暂无内容'}
                              </Text>
                            </div>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-end',
                              marginTop: 8,
                              minHeight: '20px'
                            }}>
                              <Text type="secondary" style={{ fontSize: '11px', flexShrink: 0 }}>
                                {format(new Date(doc.updatedAt), 'MM-dd HH:mm')}
                              </Text>
                              {/* 添加删除按钮 */}
                              <Button
                                size="small"
                                danger
                                icon={<Delete size={12} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDocumentDelete(doc.id);
                                }}
                                style={{
                                  fontSize: '10px',
                                  height: '20px',
                                  padding: '0 6px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px'
                                }}
                              >
                                删除
                              </Button>
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  )}
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Dashboard;
